import React, {FC} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Banner, BannerTypesEnum} from '../../../../../libs/shared/ui/src/components/banners/banner';
import {BannerAction} from '../../../../../libs/shared/ui/src/components/banners/bannerAction';
import {BannerIcon} from '../../../../../libs/shared/ui/src/components/banners/bannerIcon';
import {BannerPrioritiesEnum} from '../../../../../libs/shared/ui/src/components/banners/bannersContext';
import {BannerText} from '../../../../../libs/shared/ui/src/components/banners/bannerText';
import {
  AccountAttributeUpdateAction,
  AccountAttributeUpdateActionsEnum,
} from '../../../../helpers/models/accountAttributeUpdateHelpers';
import {AccountDetailsUndoBannerState} from '../../../../store/panels/app/banners/bannersState';

/*
 * Constants.
 */

enum AccountDetailsUndoBannerVerbsEnum {
  ADD = 'add',
  UPDATE = 'update',
  DELETE = 'delete',
}

/*
 * Props.
 */

interface AccountDetailsUndoBannerProps {
  state: AccountDetailsUndoBannerState;
  onUndoClick: () => void;
}

/*
 * Component.
 */

export const AccountDetailsUndoBanner: FC<AccountDetailsUndoBannerProps> = (props) => {
  const {
    state: {action},
    onUndoClick,
  } = props;

  return (
    <Banner bannerType={BannerTypesEnum.INLINE} priority={BannerPrioritiesEnum.TRANSIENT}>
      <BannerIcon iconName="briefcaseFilledSmall" />
      <BannerText>{getBannerText(action as AccountAttributeUpdateAction)}</BannerText>
      <BannerAction onClick={onUndoClick}>
        <Trans id="account-details-undo-banner-undo">Undo</Trans>
      </BannerAction>
    </Banner>
  );
};

/*
 * Helpers.
 */

function getBannerText(action: AccountAttributeUpdateAction) {
  const subject = getSubject(action);
  const verb = getVerb(action);

  return (
    <>
      {subject} {verb}
    </>
  );
}

function getSubject(action: AccountAttributeUpdateAction) {
  switch (action.type) {
    case AccountAttributeUpdateActionsEnum.UPDATE_NAME:
      return <Trans id="account-details-undo-banner-name">Name</Trans>;

    case AccountAttributeUpdateActionsEnum.ADD_DESCRIPTION:
    case AccountAttributeUpdateActionsEnum.UPDATE_DESCRIPTION:
    case AccountAttributeUpdateActionsEnum.DELETE_DESCRIPTION:
      return <Trans id="account-details-undo-banner-description">Description</Trans>;

    case AccountAttributeUpdateActionsEnum.ADD_CUSTOM_ATTRIBUTE:
    case AccountAttributeUpdateActionsEnum.UPDATE_CUSTOM_ATTRIBUTE:
    case AccountAttributeUpdateActionsEnum.DELETE_CUSTOM_ATTRIBUTE:
      return action.payload.customField.name;

    default:
      return '';
  }
}

function getVerb(action: AccountAttributeUpdateAction) {
  const actionTypeString = action.type.toString().toLowerCase();

  if (actionTypeString.includes(AccountDetailsUndoBannerVerbsEnum.ADD)) {
    return <Trans id="account-details-undo-banner-added">added</Trans>;
  }

  if (actionTypeString.includes(AccountDetailsUndoBannerVerbsEnum.UPDATE)) {
    return <Trans id="account-details-undo-banner-updated">updated</Trans>;
  }

  if (actionTypeString.includes(AccountDetailsUndoBannerVerbsEnum.DELETE)) {
    return <Trans id="account-details-undo-banner-deleted">deleted</Trans>;
  }

  return '';
}
